import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import TopicCard from "@/components/topic/TopicCard";
import { categories, hotRank, topics } from "@/mocks/topics";
import { formatNumber } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function TopicsList() {
  usePageMeta(
    "热议焦点 - 天涯吃瓜话题发酵追踪与舆论风向观察",
    "天涯吃瓜热议焦点话题库，按社会观察、科技圈、职场话题、娱乐现场、民生热议、文化现象等栏目追踪话题升温动态与舆论风向变化，汇总网友评论观点。",
    "天涯吃瓜热议焦点,天涯吃瓜舆论风向,天涯吃瓜网友评论,话题发酵追踪"
  );
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") ?? "all";
  const keyword = params.get("q") ?? "";
  const [draft, setDraft] = useState(keyword);

  const list = useMemo(() => {
    let arr = [...topics];
    if (cat !== "all") arr = arr.filter((t) => t.categorySlug === cat);
    if (keyword) arr = arr.filter((t) => (t.title + t.excerpt + t.tags.join("")).includes(keyword));
    return arr;
  }, [cat, keyword]);

  const allTags = useMemo(() => Array.from(new Set(topics.flatMap((t) => t.tags))).slice(0, 8), []);

  const selectCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setParams(next, { replace: true });
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    const q = draft.trim();
    if (q) next.set("q", q);
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const pickTag = (tag: string) => {
    const next = new URLSearchParams(params);
    next.set("q", tag);
    setDraft(tag);
    setParams(next, { replace: true });
  };

  const currentCat = categories.find((c) => c.slug === cat);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-400">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
        <span className="text-foreground-700">热议焦点</span>
      </nav>

      <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <span className="text-xs font-semibold tracking-wide text-primary-600">话题发酵追踪</span>
          <h1 className="mt-1 font-heading text-2xl font-black text-foreground-950 md:text-3xl">热议焦点</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-500">
            按栏目浏览正在发酵的热议焦点，追踪升温动态、舆论风向与网友观点走向。
          </p>
        </div>
        <form onSubmit={onSearch} className="relative w-full md:w-80">
          <i
            className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="搜索热议话题或关键词…"
            aria-label="搜索热议话题"
            className="h-11 w-full rounded-full border border-background-200 bg-white pl-10 pr-4 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </form>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="栏目筛选">
        {categories.map((c) => {
          const active = c.slug === cat;
          return (
            <button
              key={c.slug}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectCat(c.slug)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "bg-primary-500 text-white"
                  : "border border-background-200 bg-white text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={`${c.icon} text-sm`} aria-hidden="true"></i>
              {c.name}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <section aria-label="话题列表">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-700">
              {currentCat?.name ?? "全部"}栏目
              <span className="ml-2 rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">
                {list.length}
              </span>
            </h2>
            {keyword && (
              <button
                type="button"
                onClick={() => {
                  setDraft("");
                  const next = new URLSearchParams(params);
                  next.delete("q");
                  setParams(next, { replace: true });
                }}
                className="text-xs text-foreground-400 hover:text-primary-600"
              >
                清除搜索：{keyword}
              </button>
            )}
          </div>

          {list.length > 0 ? (
            <div className="mt-4 space-y-4">
              {list.map((t) => (
                <TopicCard key={t.id} topic={t} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-4 font-medium text-foreground-700">这个栏目暂时没有匹配的话题</p>
              <p className="mt-1 text-sm text-foreground-400">换个关键词试试，或先看看其它热议焦点</p>
              <button
                type="button"
                onClick={() => {
                  setDraft("");
                  setParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-5 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-medium text-white hover:bg-primary-600"
              >
                查看全部热议焦点
              </button>
            </div>
          )}
        </section>

        <aside className="space-y-6" aria-label="侧栏">
          <div className="rounded-2xl border border-background-200 bg-white p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
              热议 TOP5
            </h3>
            <ol className="mt-4 space-y-3">
              {hotRank.slice(0, 5).map((t, i) => (
                <li key={t.id}>
                  <Link
                    to={`/topic/${t.id}`}
                    className="group flex items-center gap-3 rounded-lg p-1 transition-colors hover:bg-background-50"
                  >
                    <span className="w-4 shrink-0 font-heading text-sm font-bold text-foreground-300">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground-800 group-hover:text-primary-600">{t.title}</p>
                    </div>
                    <span className="shrink-0 text-xs text-foreground-400">{formatNumber(t.heat)}</span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-2xl border border-background-200 bg-white p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <i className="ri-price-tag-3-line text-accent-500" aria-hidden="true"></i>
              热词标签
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => pickTag(tag)}
                  className="whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800 transition hover:bg-accent-100 hover:text-accent-800"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}